import React, { memo, useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import styled from '@2pulse/responsive';
import { DateTime } from 'luxon';
import { useForm } from 'react-hook-form';
import LinearGradient from 'react-native-linear-gradient';
import { Button, Card, CardButtonActionMode, FilterButton, Form, HeaderGradient, MiniCard, Row } from '@app/components';
import { DateTimePicker } from '@app/components/form/DateTimePicker.web';
import { CheckPermissions } from '@app/components/CheckPermissions';
import { StatusDot } from '@app/components/decor/StatusDot';
import SvgStats from '@app/components/icons/SvgStats';
import { SlantTitle } from '@app/components/typography';
import { extractApiGenericError, normalize } from '@app/helpers';
import { useDynamicSizes, useToast } from '@app/hooks';
import { useModal } from '@app/hooks/useModal';
import { fiscalApi, triptychSelectors } from '@app/store';
import { useAppSelector } from '@app/store/hooks';
import { theme } from '@app/theme';
import type { FiscalVerifyReport, IFiscalRegisterSummary } from '@tickeat/types';
import { COULEUR_ETAT } from './couleurs';
import { DeclareLostModal } from './DeclareLostModal';
import { bornesDExport, couleurEtatRegistre, detailDeVerification, libelleEtatRegistre, peutDeclarerDisparue } from './fiscal.logic';
import { FiscalEcoleOrders } from './FiscalEcoleOrders';

type Props = { register?: Partial<IFiscalRegisterSummary>; toggle: (mode: CardButtonActionMode) => void };

const MODAL_ID = 'fiscal-declare-lost';

const dateFr = (iso: string | null | undefined) => (iso ? DateTime.fromISO(iso).toFormat('dd/MM/yyyy HH:mm') : '—');

// 6.11.4.3 (contre-audit E-22) : le contrôle d'intégrité additionnel de l'engine doit
// conserver l'enregistrement de ses erreurs — cette ligne en est la restitution, qu'il ait
// trouvé un défaut ou non. Même formulation que sur la carte d'archive (`FiscalArchiveCard`).
const libelleDerniereVerification = (v: NonNullable<IFiscalRegisterSummary['lastVerification']>): string => {
  const base = `Dernière vérification : le ${dateFr(v.at)}, par ${v.by}`;
  return v.ok ? `${base}, OK` : `${base} — ${v.defect?.reason ?? 'défaut non qualifié'}`;
};

// Fix round 1, I1 : une vérification VERTE n'efface jamais le dernier DÉFAUT constaté — seule
// une vérification ROUGE ultérieure le remplace. Restitué à part de « Dernière vérification »,
// qui peut donc être verte alors qu'un défaut reste visible ici.
const libelleDernierDefaut = (d: NonNullable<IFiscalRegisterSummary['lastDefect']>): string =>
  `Dernier défaut constaté le ${dateFr(d.at)}, par ${d.by} : ${d.reason}`;

export const FiscalRegisterCard = memo(({ register, toggle }: Props) => {
  const { subContainerHeight } = useDynamicSizes();
  const isOpen = useAppSelector(triptychSelectors.selectIsOpen);
  const boutique = useAppSelector((state) => state.app.selectedShop);
  const toast = useToast();
  const modal = useModal(MODAL_ID);

  // Le triptyque garde l'instantané cliqué dans la liste : après une
  // déclaration de disparition, il montrerait encore « Active » et sa zone
  // dangereuse. La fiche relit donc le registre dans le cache de la liste, que
  // `declareFiscalRegisterLost` invalide.
  //
  // `skip` sans boutique : la gateway porte la boutique par l'en-tête `x-shop`
  // et répond « Bad Request Exception » quand il manque (retour du 9 sept.).
  const { data: registres } = fiscalApi.useReadFiscalRegistersQuery(undefined, { skip: !boutique });

  const [verifier, { isLoading: verification }] = fiscalApi.useVerifyFiscalRegisterMutation();
  const [exporter, { isLoading: exportation }] = fiscalApi.useExportFiscalRegisterMutation();
  const [declarer, { isLoading: declaration }] = fiscalApi.useDeclareFiscalRegisterLostMutation();
  const [rapport, setRapport] = useState<FiscalVerifyReport | null>(null);

  const formMethods = useForm<{ fromDate: string | null; toDate: string | null }>({ defaultValues: { fromDate: null, toDate: null } });
  const [fromDate, toDate] = formMethods.watch(['fromDate', 'toDate']);

  // La fiche n'est pas remontée quand on change de caisse (pas de `key` côté
  // écran) : sans cette purge, le rapport d'intégrité de la caisse A resterait
  // affiché sous la caisse B.
  useEffect(() => { setRapport(null); }, [register?.registerId]);

  // ÉTAT VIDE : LE PANNEAU DES COMMANDES D'ENTRAÎNEMENT (revue finale 9c-B, I2).
  //
  // Il vivait uniquement en tête de la colonne des ARCHIVES, translatée hors de
  // l'écran tant que le triptyque est fermé : il fallait ouvrir une caisse pour
  // le voir, alors qu'il ne parle pas de caisse. Cette colonne-ci, elle, est
  // visible d'emblée — le seul chemin du dashboard vers le retrait du mode école
  // est donc là où on le cherche. Il reste AUSSI en tête des archives, pour qui
  // travaille caisse ouverte.
  if (!register?.registerId) {
    return (
      <Card>
        <Gradient />
        <Card.TopHeader><Card.TopHeaderLabel>Caisse fiscale</Card.TopHeaderLabel></Card.TopHeader>
        <Card.Container height={subContainerHeight}>
          <Card.Background source={require('@app/assets/img/cardbackground.png')} />
          <Card.Content>
            <Invite>Sélectionnez une caisse dans la liste.</Invite>
            <Card.LineBreak height={24} />
            <FiscalEcoleOrders />
          </Card.Content>
        </Card.Container>
      </Card>
    );
  }

  const r = registres?.find((x) => x.registerId === register.registerId) ?? (register as IFiscalRegisterSummary);
  const disparue = peutDeclarerDisparue(r, new Date().toISOString());
  const detail = rapport ? detailDeVerification(rapport) : null;

  const onVerifier = async () => {
    try { setRapport(await verifier(r.registerId).unwrap()); }
    catch (e: any) { toast.error(extractApiGenericError(e) ?? 'La vérification a échoué.'); }
  };

  const onExporter = async () => {
    try { await exporter({ registerId: r.registerId, registerCode: r.registerCode, ...bornesDExport(fromDate, toDate) }).unwrap(); }
    catch (e: any) { toast.error(extractApiGenericError(e) ?? 'L’export a échoué.'); }
  };

  const onDeclarer = async () => {
    try {
      const { already } = await declarer(r.registerId).unwrap();
      toast.success(already
        ? `La caisse ${r.registerCode} était déjà déclarée disparue.`
        : `La caisse ${r.registerCode} est déclarée disparue ; son archive finale est en cours de constitution.`);
    } catch (e: any) { toast.error(extractApiGenericError(e) ?? 'La déclaration a échoué.'); }
    modal.close();
  };

  const copierCle = async () => {
    try { await navigator.clipboard.writeText(r.publicKey); toast.success('Clé publique copiée.'); }
    catch { toast.error('Impossible de copier la clé.'); }
  };

  return (
    <Card>
      <Gradient />

      {/* Le bandeau nomme la COLONNE, pas la caisse : le code de la caisse est
          le titre juste dessous, il était écrit deux fois (retour du 9 sept.). */}
      <Card.TopHeader><Card.TopHeaderLabel>Caisse fiscale</Card.TopHeaderLabel></Card.TopHeader>

      <Card.Container height={subContainerHeight}>
        <Card.Background source={require('@app/assets/img/cardbackground.png')} />

        <Card.Content>
          <Card.Header>
            <Row justify="space-between" align="center">
              <Card.HeaderTitles>
                <Card.Title numberOfLines={2}>Caisse {r.registerCode}</Card.Title>
                <Card.Subtitle>Terminal {r.terminalId.slice(0, 8).toUpperCase()} · créée le {dateFr(r.createdIso)}</Card.Subtitle>
              </Card.HeaderTitles>
              <Button title={isOpen ? 'Fermer' : 'Voir'} bordered={isOpen} onPress={() => toggle(isOpen ? 'CLOSE' : 'UPDATE')} />
            </Row>

            <Card.HeaderValues>
              <Card.HeaderValuesItem>
                <Card.Key numberOfLines={1}>État</Card.Key>
                <View testID="etat-registre" style={{ alignItems: 'center' }}>
                  <Row align="center">
                    <StatusDot color={COULEUR_ETAT[couleurEtatRegistre(r.status)]} />
                    <Card.Value> {libelleEtatRegistre(r.status)}{r.silent ? ' · silencieuse' : ''}</Card.Value>
                  </Row>
                  {r.status !== 'ACTIVE' && (
                    <SousLigne>depuis le {dateFr(r.statusIso)}{r.statusReason ? ` — ${r.statusReason}` : ''}</SousLigne>
                  )}
                </View>
              </Card.HeaderValuesItem>
              <Card.HeaderValuesItem>
                <Card.Key numberOfLines={1}>Dernière réplication</Card.Key>
                <Card.Value>{dateFr(r.lastRecordAt)}</Card.Value>
              </Card.HeaderValuesItem>
              <Card.HeaderValuesItem>
                <Card.Key numberOfLines={1}>Dernier Z</Card.Key>
                <Card.Value>{dateFr(r.lastDailyClosureIso)}</Card.Value>
              </Card.HeaderValuesItem>
            </Card.HeaderValues>
          </Card.Header>
        </Card.Content>

        <MiniCard>
          <MiniCard.Tabs>
            <MiniCard.TabsHolder>
              <MiniCard.Tab Icon={SvgStats} isActive />
            </MiniCard.TabsHolder>
          </MiniCard.Tabs>
          <MiniCard.Container>
            <MiniCard.Content>

              <Card.LineBreak height={24} />

              {/* IDENTITÉ */}
              <SlantTitle label="Identité" color={theme.colors.gold} />
              <Section>
                <Carte>
                  <Row justify="space-between" align="center">
                    <CarteTitre>Clé publique de la caisse</CarteTitre>
                    <FilterButton title="Copier la clé" onPress={copierCle} testID="copier-cle-caisse" />
                  </Row>
                  <CarteSousTitre>Identifiant de clé : {r.keyId}</CarteSousTitre>
                  <ClePublique selectable testID="cle-publique">{r.publicKey}</ClePublique>
                </Carte>
              </Section>

              {/* INTÉGRITÉ */}
              <SlantTitle label="Intégrité" color={theme.colors.gold} />
              <Section>
                {/* Fix round 2 (revue, E-22) : `verifyRegister` écrit désormais
                    (`lastVerification`/`lastDefect`) — la route est passée à
                    `dashboard::fiscal.manage`, le bouton doit suivre sous peine d'un 403
                    pour un utilisateur `fiscal.read` qui le voyait encore. */}
                <CheckPermissions permissions={['dashboard::fiscal.manage']}>
                  <Row justify="flex-end" align="center">
                    <FilterButton title="Vérifier l’intégrité" loading={verification} onPress={onVerifier} testID="verifier-integrite" />
                  </Row>
                </CheckPermissions>

                {r.lastVerification && (
                  <Verification ok={r.lastVerification.ok} testID="derniere-verification-registre">
                    {libelleDerniereVerification(r.lastVerification)}
                  </Verification>
                )}

                {r.lastDefect && (
                  <Verification ok={false} testID="dernier-defaut-registre">
                    {libelleDernierDefaut(r.lastDefect)}
                  </Verification>
                )}

                {detail && (
                  <View testID="rapport-verification" style={{ rowGap: normalize(10) }}>
                    <Bandeau ok={detail.ok}>
                      <BandeauTexte ok={detail.ok}>{detail.titre}</BandeauTexte>
                    </Bandeau>
                    {detail.lignes.map((l) => (
                      <LigneRapport key={l.cle}>
                        <PastilleHolder>
                          <StatusDot color={l.ok ? COULEUR_ETAT.vert : COULEUR_ETAT.rouge} />
                        </PastilleHolder>
                        <LigneCorps>
                          <LigneTitre>{l.libelle}</LigneTitre>
                          <LigneDetail ok={l.ok}>{l.detail}</LigneDetail>
                        </LigneCorps>
                      </LigneRapport>
                    ))}
                  </View>
                )}
              </Section>

              {/* EXPORT DE PÉRIODE */}
              <SlantTitle label="Export de période" color={theme.colors.gold} />
              <Section>
                {/* `onWhite` est le rendu éprouvé de ce champ sur fond sombre
                    (`ServiceList`), mais il masque le libellé : on l'écrit au-dessus. */}
                <Form formMethods={formMethods}>
                  <Row justify="space-between" align="flex-start">
                    <Champ>
                      <ChampLabel>Du</ChampLabel>
                      <DateTimePicker name="fromDate" type="date" label="Du" onWhite />
                    </Champ>
                    <Champ>
                      <ChampLabel>Au</ChampLabel>
                      <DateTimePicker name="toDate" type="date" label="Au" onWhite />
                    </Champ>
                  </Row>
                </Form>
                <Row justify="flex-end" align="center">
                  <FilterButton title="Exporter en JSON" loading={exportation} onPress={onExporter} testID="exporter-periode" />
                </Row>
              </Section>

              {/* ZONE DANGEREUSE */}
              {r.status === 'ACTIVE' && (
                <CheckPermissions permissions={['dashboard::fiscal.manage']}>
                  <SlantTitle label="Zone dangereuse" color={theme.colors.crimson} />
                  <Section>
                    <CarteDanger>
                      {/* `Card.HelpText` ne laisse pas passer de `testID` exploitable :
                          le repère est porté par une `View` qui l'enveloppe. */}
                      {!disparue.possible ? (
                        <View testID="motif-disparue">
                          <DangerTexte>{disparue.motif}</DangerTexte>
                        </View>
                      ) : (
                        <DangerTexte>
                          Ce geste est irréversible : la caisse sera fermée et son archive finale constituée avec les seules pièces déjà répliquées.
                        </DangerTexte>
                      )}
                      <Row justify="flex-end" align="center">
                        <BoutonDanger actif={disparue.possible} disabled={!disparue.possible} onPress={() => modal.open()} testID="declarer-disparue">
                          <BoutonDangerTexte actif={disparue.possible}>Déclarer la caisse disparue</BoutonDangerTexte>
                        </BoutonDanger>
                      </Row>
                    </CarteDanger>
                    <DeclareLostModal id={MODAL_ID} registerCode={r.registerCode} close={modal.close} onConfirm={onDeclarer} isLoading={declaration} />
                  </Section>
                </CheckPermissions>
              )}

              <Card.LineBreak height={34} />

            </MiniCard.Content>
          </MiniCard.Container>
        </MiniCard>

      </Card.Container>
    </Card>
  );
});

// Même dégradé de tête que `ServiceCard` : sans lui la carte démarre à ras du
// bandeau de la liste.
const Gradient = () => (
  <HeaderGradient>
    <LinearGradient
      start={{ x: 0.5, y: 0 }}
      end={{ x: 0.5, y: 1 }}
      locations={[0, 1]}
      colors={['rgba(253, 255, 252, 0)', 'rgba(253, 255, 252, 1)']}
      style={{ flex: 1, height: theme.sizes.listHeaderHeight }} />
  </HeaderGradient>
);

const Invite = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(20),
  fontWeight: '500',
  textAlign: 'center',
  color: theme.colors.grey,
}));

const SousLigne = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  textAlign: 'center',
  paddingTop: normalize(3),
  color: theme.colors.grey,
}));

// Chaque section vit sous son `SlantTitle`, avec la gouttière de `Card.Content`.
const Section = styled.View({
  paddingHorizontal: normalize(34),
  paddingTop: normalize(21),
  paddingBottom: normalize(34),
  rowGap: normalize(13),
});

// Même carte que `OrderCardContainer` : fond translucide, coins très arrondis.
const Carte = styled.View(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.colors.translucid,
  borderRadius: normalize(42),
  padding: normalize(21),
  rowGap: normalize(8),
}));

const CarteTitre = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '700',
  color: theme.colors.babypowder,
}));

const CarteSousTitre = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.grey,
}));

// La clé est un bloc de base64 : monospace, sélectionnable, et volontairement
// plus petite que le corps de texte — on la copie, on ne la lit pas.
const ClePublique = styled.Text(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: normalize(13),
  lineHeight: normalize(18),
  color: theme.colors.babypowder,
  opacity: 0.7,
}));

const Verification = styled.Text<{ ok: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  lineHeight: normalize(20),
  color: props.ok ? theme.colors.grey : theme.colors.crimson,
}));

const Bandeau = styled.View<{ ok: boolean }>(({ props }) => ({
  width: '100%',
  borderRadius: normalize(42),
  paddingVertical: normalize(10),
  paddingHorizontal: normalize(21),
  backgroundColor: props.ok ? 'rgba(42, 168, 49, 0.18)' : 'rgba(220, 0, 0, 0.18)',
  borderWidth: 1,
  borderColor: props.ok ? COULEUR_ETAT.vert : COULEUR_ETAT.rouge,
}));

const BandeauTexte = styled.Text<{ ok: boolean }>(({ props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '700',
  textAlign: 'center',
  color: props.ok ? COULEUR_ETAT.vert : COULEUR_ETAT.rouge,
}));

const LigneRapport = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  columnGap: normalize(13),
  backgroundColor: theme.colors.translucid,
  borderRadius: normalize(20),
  paddingVertical: normalize(13),
  paddingHorizontal: normalize(18),
}));

const PastilleHolder = styled.View({ paddingTop: normalize(5) });

const LigneCorps = styled.View({ flexShrink: 1, rowGap: normalize(2) });

const LigneTitre = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '600',
  color: theme.colors.babypowder,
}));

const LigneDetail = styled.Text<{ ok: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: props.ok ? theme.colors.grey : COULEUR_ETAT.rouge,
}));

const Champ = styled.View({ width: '48%', rowGap: normalize(5) });

const ChampLabel = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '600',
  color: theme.colors.celadonblue,
}));

const CarteDanger = styled.View(({ theme }) => ({
  width: '100%',
  borderRadius: normalize(42),
  padding: normalize(21),
  rowGap: normalize(13),
  borderWidth: 1,
  borderColor: theme.colors.crimson,
  backgroundColor: 'rgba(220, 0, 0, 0.06)',
}));

const DangerTexte = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  lineHeight: normalize(20),
  color: theme.colors.babypowder,
}));

// `FilterButton` ne sait pas être rouge, et son variant `small` se rend en rond
// de 34 px qui écrase son libellé : la pilule de la zone dangereuse est à part.
const BoutonDanger = styled.TouchableOpacity<{ actif?: boolean }>(({ theme, props }) => ({
  height: 34,
  borderRadius: 100,
  paddingHorizontal: normalize(16),
  justifyContent: 'center',
  alignItems: 'center',
  borderWidth: 2,
  backgroundColor: props.actif ? theme.colors.crimson : 'transparent',
  borderColor: props.actif ? theme.colors.crimson : theme.colors.satanGray,
}));

const BoutonDangerTexte = styled.Text<{ actif?: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '600',
  color: props.actif ? theme.colors.babypowder : theme.colors.satanGray,
}));
